"use client";

import React, { createContext, useCallback, useMemo, useState } from "react";

import { OtpModal } from "@/components/otp/otp-modal";
import { logError } from "@/lib/logError";
import { OtopRequestState } from "@generated/redux/services/generated/api";

type OtpStep = "trust" | "code-entry" | "verifying" | "exhausted";

interface OtpConfig {
  showTrustScreen?: boolean;
  title?: string;
  description?: string;
  recipientName?: string;
  getTrustCodeCta?: string;
  cancelCta?: string;
  confirmCta?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
  flowType?: "recipient" | "payment";
  // Payment-specific context
  paymentAmount?: number;
  paymentCurrency?: string;
  expiryTime?: string; // ISO format timestamp from OTP response
  // API handlers
  onProceedToCodeEntry?: () => Promise<string | void>;
  onSubmitCode?: (code: string) => Promise<{ state: OtopRequestState }>;
  onResendCode?: () => Promise<string | void>;
}

export interface OtpContextValue {
  isOpen: boolean;
  currentStep: OtpStep;
  config: OtpConfig | null;
  otpCode: string;
  isOtpInProgress: boolean;
  isLoadingTrustScreen: boolean;
  showOtpModal: (config: OtpConfig) => void;
  closeModal: () => void;
  submitCode: (code: string) => Promise<void>;
  resendCode: () => Promise<void>;
  proceedToCodeEntry: () => void;
}

const OtpContext = createContext<OtpContextValue | undefined>(undefined);

interface OtpProviderProps {
  children: React.ReactNode;
}

export function OtpProvider({ children }: OtpProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState<OtpStep>("trust");
  const [config, setConfig] = useState<OtpConfig | null>(null);
  const [otpCode, setOtpCode] = useState("");
  const [isOtpInProgress, setIsOtpInProgress] = useState(false);
  const [isLoadingTrustScreen, setIsLoadingTrustScreen] = useState(false);
  const [_otpState, setOtpState] = useState<OtopRequestState | null>(null);
  const [_errorMessage, setErrorMessage] = useState<string | null>(null);

  const showOtpModal = useCallback((newConfig: OtpConfig) => {
    setConfig(newConfig);
    setIsOpen(true);
    setOtpCode("");
    setIsOtpInProgress(true);
    // Determine initial step based on config
    if (newConfig.showTrustScreen) {
      setCurrentStep("trust");
    } else {
      setCurrentStep("code-entry");
    }
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    // Delay resetting state to prevent flickering during modal close animation
    setTimeout(() => {
      setCurrentStep("trust");
      setOtpCode("");
      setConfig(null);
      setIsOtpInProgress(false);
      setOtpState(null);
      setErrorMessage(null);
    }, 300); // Match typical modal animation duration
  }, []);

  const proceedToCodeEntry = useCallback(async () => {
    // Call external handler if provided
    if (config?.onProceedToCodeEntry) {
      try {
        setIsLoadingTrustScreen(true);
        const expiryTime = await config.onProceedToCodeEntry();
        // Update config with expiryTime if returned
        if (expiryTime) {
          setConfig((prev) => (prev ? { ...prev, expiryTime } : prev));
        }
        // Only proceed to code entry if no error was thrown
        setCurrentStep("code-entry");
        setIsLoadingTrustScreen(false);
      } catch (error) {
        logError("Error proceeding to code entry", error, {
          flow: "otp-provider",
          step: "proceed-to-code-entry",
          flowType: config?.flowType,
        });
        setIsLoadingTrustScreen(false);
        // Don't transition to code-entry if there was an error
        // Error toast is already shown by the component
        return;
      }
    } else {
      // No handler provided, just transition
      setCurrentStep("code-entry");
    }
  }, [config]);

  const submitCode = useCallback(
    async (code: string) => {
      setOtpCode(code);
      setCurrentStep("verifying");
      setErrorMessage(null);

      try {
        // Call external handler if provided
        if (config?.onSubmitCode) {
          const result = await config.onSubmitCode(code);
          setOtpState(result.state);

          // Handle different states using switch statement
          switch (result.state) {
            case OtopRequestState.Completed:
              // Success - close modal and call onSuccess
              closeModal();
              setTimeout(() => {
                if (config?.onSuccess) {
                  config.onSuccess();
                }
              }, 400); // 300ms for modal animation + 100ms buffer
              break;

            case OtopRequestState.Failed:
              // Wrong code - clear input, reset to code-entry
              setOtpCode("");
              setCurrentStep("code-entry");
              setErrorMessage("Incorrect code entered");
              break;

            case OtopRequestState.Expired:
              // Code expired - reset to code-entry, prompt resend
              setOtpCode("");
              setCurrentStep("code-entry");
              setErrorMessage(
                "Code expired. Click 'Resend code' to receive a new one."
              );
              break;

            case OtopRequestState.Exhausted:
              // Too many attempts - show exhausted screen
              setCurrentStep("exhausted");
              setErrorMessage("Too many attempts. Please contact support.");
              break;

            case OtopRequestState.Pending:
              // Still pending - continue waiting
              setCurrentStep("verifying");
              break;

            default:
              // Unknown state - reset to code-entry
              setOtpCode("");
              setCurrentStep("code-entry");
              setErrorMessage("An error occurred. Please try again.");
              break;
          }
        } else {
          // No external handler - use old behavior (for backwards compatibility)
          await new Promise((resolve) => setTimeout(resolve, 1000));
          closeModal();
          setTimeout(() => {
            if (config?.onSuccess) {
              config.onSuccess();
            }
          }, 400);
        }
      } catch (error) {
        logError("Error submitting OTP code", error, {
          flow: "otp-provider",
          step: "submit-code",
          flowType: config?.flowType,
        });
        const errorMessage = (error as Error).message;

        // Check if error message is an OTP state enum value
        if (errorMessage === OtopRequestState.Failed) {
          setOtpCode("");
          setCurrentStep("code-entry");
        } else if (errorMessage === OtopRequestState.Expired) {
          setOtpCode("");
          setCurrentStep("code-entry");
        } else if (errorMessage === OtopRequestState.Exhausted) {
          setCurrentStep("exhausted");
        } else {
          // Generic error
          setOtpCode("");
          setCurrentStep("code-entry");
          setErrorMessage("Failed to verify code. Please try again.");
        }
      }
    },
    [config, closeModal]
  );

  const resendCode = useCallback(async () => {
    // Reset error state and input fields
    setErrorMessage(null);
    setOtpCode("");

    // Call external handler if provided
    if (config?.onResendCode) {
      try {
        const expiryTime = await config.onResendCode();
        // Update config with new expiryTime if returned
        if (expiryTime) {
          setConfig((prev) => (prev ? { ...prev, expiryTime } : prev));
        }
      } catch (error) {
        logError("Error resending OTP code", error, {
          flow: "otp-provider",
          step: "resend-code",
          flowType: config?.flowType,
        });
        setErrorMessage("Failed to resend code. Please try again.");
      }
    }
  }, [config]);

  // Memoize the context value (render-stability.md Rule 3). An inline object
  // literal here re-renders every useOtp() consumer on every OtpProvider state
  // change; combined with the recipient page's auto-trust-on-mount effect that
  // fires showOtpModal during initial render, that cascade tripped React #185
  // ("Maximum update depth exceeded") in production (CUSTOMER-UI-3K, 2026-06-01).
  const value = useMemo<OtpContextValue>(
    () => ({
      isOpen,
      currentStep,
      config,
      otpCode,
      isOtpInProgress,
      isLoadingTrustScreen,
      showOtpModal,
      closeModal,
      submitCode,
      resendCode,
      proceedToCodeEntry,
    }),
    [
      isOpen,
      currentStep,
      config,
      otpCode,
      isOtpInProgress,
      isLoadingTrustScreen,
      showOtpModal,
      closeModal,
      submitCode,
      resendCode,
      proceedToCodeEntry,
    ]
  );

  return (
    <OtpContext.Provider value={value}>
      {}
      {children}
      <OtpModal />
    </OtpContext.Provider>
  );
}

export { OtpContext };
